import { Context } from '@obvio/app'
import { serverClient } from '@obvio/server'
import { slugify } from '@obvio/utils'

import { schema } from '@/api/schema'
import { ArtistPage } from '@/components/Artist/ArtistPage'
import { Cursor } from '@/components/Cursor'
import { Layout } from '@/components/Layout'

import type { GetStaticProps, Schema } from '@obvio/app'
import type { DeepPick } from '@obvio/utils'
import type { GetStaticPaths } from 'next'
import type { ReactElement } from 'react'

const MERCHANT_QUERY_SELECT = {
  id: true,
  name: true,
  description: true,
  image: true,
  cv: true,
  videos: true,
  products: {
    id: true,
    title: true,
    slug: true,
    images: true,
    description: true,
    year: true,
  },
  criticism: true,
  press: true,
  awards: true,
  exhibitions: true,
} as const

export type Data = DeepPick<Schema['Merchant'], typeof MERCHANT_QUERY_SELECT>

export const getStaticPaths: GetStaticPaths = async () => {
  const client = await serverClient(schema, { isAdmin: true })

  const merchants = await client.query('getMerchants', {
    select: {
      name: true,
    },
  })

  const paths = merchants.map((merchant) => ({
    params: {
      name: slugify(merchant.name),
    },
  }))

  return {
    paths,
    fallback: false,
  }
}

export const getStaticProps: GetStaticProps<
  any,
  { id: string; name: string }
> = async (context) => {
  const client = await serverClient(schema, { ...context, isAdmin: true })

  const merchant = await client.query('getMerchantViaSlugName', {
    args: {
      name: context.params?.name ?? '',
    },
    select: MERCHANT_QUERY_SELECT,
  })

  if (!merchant) {
    return { notFound: true }
  }

  return {
    ...client.ssrData(merchant),
    revalidate: 1000,
  }
}

function Page(artist: Data): ReactElement {
  return (
    <Context name="page" render="div">
      <Cursor kind="black" />
      <Layout>
        <ArtistPage {...artist} />
      </Layout>
    </Context>
  )
}

export default Page
